import {DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Observable} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {BulkImportResultView, handleBulkImportError,} from '../bulk-import-result/bulk-import-result.utils';
import {
    BulkUploadSupportedField,
    BulkUploadSupportedValuesTip,
} from '../components/bulk-upload-supported-values/bulk-upload-supported-values.model';
import {BulkUploadShellMode} from '../bulk-upload-shell/bulk-upload-shell.component';

/**
 * Module-specific BulkUploadShell presentation.
 * API/upload behaviour stays in BulkUploadFlowOptions.
 */
export type BulkUploadShellConfig = {
    mode?: BulkUploadShellMode;
    title?: string;
    intro?: string;
    accept?: string;
    showDownloadTemplate?: boolean;
    showGuidelines?: boolean;
    supportedFields?: BulkUploadSupportedField[];
    supportedTip?: BulkUploadSupportedValuesTip | string;
    supportedTitle?: string;
};

export type BulkUploadFlowOptions = {
    /** Failure CSV filename fallback when the API omits one. */
    failureFilename: string;
    /** Toastr fallback when the API omits `message`. */
    defaultSuccessMessage: string;
    /** POST the prepared FormData (sync request — no jobs/polling). */
    upload: (formData: FormData) => Observable<unknown>;
    /** Override FormData construction (e.g. extra fields). Defaults to `{file}`. */
    buildFormData?: (file: File) => FormData;
};

/**
 * Shared Bulk Upload dialog + submit flow (Vehicle Monitoring reference).
 * Parents bind shell inputs to this state and call open/close/submit.
 *
 * Pass the owning component's DestroyRef so in-flight uploads unsubscribe on destroy.
 */
export class BulkUploadFlow {
    dialogOpen = false;
    selectedFile: File | null = null;
    uploading = false;
    result: BulkImportResultView | null = null;

    constructor(
        private readonly toastr: ToastrService,
        private readonly options: BulkUploadFlowOptions,
        private readonly destroyRef?: DestroyRef,
    ) {
    }

    open(): void {
        this.selectedFile = null;
        this.uploading = false;
        this.dialogOpen = true;
    }

    close(): void {
        if (this.uploading) {
            return;
        }
        this.dialogOpen = false;
        this.selectedFile = null;
    }

    selectFile(file: File): void {
        this.selectedFile = file;
    }

    /**
     * Submit selected (or provided) file.
     * On success: closes dialog, clears result, toasts, invokes onSuccess.
     * On row-level failure: closes dialog and sets `result` for BulkImportResult.
     */
    submit(file?: File, onSuccess?: (response: any) => void): void {
        const uploadFile = file || this.selectedFile;
        if (!uploadFile || this.uploading) {
            return;
        }

        this.result = null;
        this.uploading = true;
        const formData = this.options.buildFormData
            ? this.options.buildFormData(uploadFile)
            : defaultBulkUploadFormData(uploadFile);

        let upload$ = this.options.upload(formData);
        if (this.destroyRef) {
            upload$ = upload$.pipe(takeUntilDestroyed(this.destroyRef));
        }

        upload$.subscribe({
            next: (response: any) => {
                this.uploading = false;
                this.result = null;
                this.dialogOpen = false;
                this.selectedFile = null;
                this.toastr.success(
                    response?.message || this.options.defaultSuccessMessage,
                    'Success',
                );
                onSuccess?.(response);
            },
            error: (err: any) => {
                this.uploading = false;
                this.result = handleBulkImportError(
                    err,
                    this.toastr,
                    this.options.failureFilename,
                );
                this.dialogOpen = false;
                this.selectedFile = null;
            },
        });
    }
}

export function defaultBulkUploadFormData(file: File): FormData {
    const formData = new FormData();
    formData.append('file', file);
    return formData;
}
